<script lang="ts">
  import { _ } from 'svelte-i18n';
  import ProgressionChart from './ProgressionChart.svelte';
  import { getLibxert, getWorker, killWorker } from '../../xert-wasm';
  import ProgressBar from './ProgressBar.svelte';
  import type { CalendarSettings } from '@/Util/api';
  import { getForecastInputsForChart, mergeAdaptedForecast, mergeForecastInputs } from '@/Util/utils';
  import type { MyFitnessStores } from '../stores';
  import { getContext } from 'svelte';
  import Am5Loader from '../../Util/Am5Loader.svelte';
  import {Skeleton} from '@/Common/';
  import { fromUnixTime, max, startOfDay } from 'date-fns';
  import { cn } from '@/lib/utils';
  import type { AchievabilityStatus } from '@/Program/modules/ProgramAchievability';
  import { slide } from 'svelte/transition';
  import { defaultForecastInfo, type ForecastInfo } from '@/Common/constants';
  import type WeatherAndMedian from '../WeatherAndMedian.svelte';

  const myFitness = getContext<MyFitnessStores>('myFitness')
  const {userParams, trainingPlan} = myFitness
  const { trainingMedians } = getContext<WeatherAndMedian>('medianAndWeather')
  
  export let forecastInfo: ForecastInfo = {...defaultForecastInfo}
  export let forecastInputs;

  let className: string = undefined
  export {className as class}
  export let id = "training-forecaster"
  export let cal_settings: CalendarSettings
  export let adapt = false
  export let hidden = false

  let forecastMessage = ""; // ? Not entirely sure if this is required anymore
  let forecastData = [];
  let forecastProgress = 0.0;
  let forecastShowPower = null;
  let chartRange = null;
  let chartTimer = null;
  let pendingData = [];

  export async function trainingForecast() {
    console.log("trainingForecast")
    if (forecastInputs == null) {
        console.error('forecastInputs is null');
        return;
    }
    forecastInfo = {...defaultForecastInfo}

      
    let data = forecastInputs;
    const forecastSettings = data.options
    
    data = mergeForecastInputs(data, cal_settings.defaults, cal_settings.availabilities, $trainingMedians)
    console.log('trainingForecast....', {data, defaults: cal_settings.defaults, forecastSettings})

    const inputs = { ...data, options: forecastSettings };

    if (!data || !data.days) {
        // Something went wrong on the server
        forecastInfo = {
            ...forecastInfo,
            resultCode: 300,
            status: "error",
            results: null,
            running: false,
            done: true,
        }     
        // forecastMessage = "Server Error, please try again later."
        return
    }

    forecastInfo.running = true;
    forecastProgress = 0.01;
    forecastMessage = 'Working...';
    forecastShowPower = '';

    let prependData = [];
    let i0 = 0;
    if (adapt) {
        // Get data for existing training plan, including past completed activities.
        const mergedInput = await getForecastInputsForChart($trainingPlan.forecastResults, cal_settings.defaults, cal_settings.availabilities)

        const t0 = Math.round(startOfDay(fromUnixTime(data.days[0]['t'])).getTime()/1000);
        // Keep only past data
        const prependInputs = mergedInput.days.filter((day)=>day.t < t0)
        // TODO: Make sure logic for Today is consistent: keep existing placeholder today, or generate new one?

        const inputsForChart = { ...mergedInput, days: [...prependInputs, ...data.days], options: forecastSettings, keepPinned: true, getBackOnTrack: true };
        const result = (await getLibxert()).recalculate(inputsForChart)
        prependData = result.days.filter((day)=>day.t < t0)
        i0 = prependData.length;

        inputs['keepPinned'] = true;
    }

    killWorker()
    let dayIndex = 0;
    const results = await (getWorker()).training_forecast(inputs, (message)=>{
        if (message['days']) {
            pendingData = [...prependData, ...message['days']]
            if (!chartTimer) {
                chartTimer = window.setTimeout(()=>{   
                    if (forecastInfo.running)
                        forecastData = pendingData
                        chartTimer = null
                    }, 1000)
            }
            if (dayIndex > 0  && message['days'][dayIndex]?.fp) forecastShowPower = ' ⚡️ Focus Power: ' + Math.round(message['days'][dayIndex]?.fp) + "W.";
            else forecastShowPower = '';
        }
        if (message['debug']) {
            const debug = message['debug'];
            forecastProgress = debug['progress'];
            forecastMessage = debug['msg'] + "." + forecastShowPower;
            if (debug['i1']) {
                chartRange = [i0+debug['i0'], i0+debug['i1']];
                dayIndex = i0+debug['i1'];
            }
        }
    })
    pendingData = null
    if (chartTimer) {
        clearTimeout(chartTimer)
        chartTimer = null
    }


    if (results['error'] != null) {
        forecastInfo = {
            ...forecastInfo,
            resultCode: results['error'] == 'taper' ? 301 : 302,
            status: "error",
            results: null,
            running: false,
            done: true,
        }
        // forecastMessage = (results['error'] == 'taper')?"Could not create taper week": "A training plan that reaches your target could not be found.";
        chartRange = null;
        return
    }

    // Keep copy of original inputs so that optimizer can be re-run in original state, for example when sorting workouts.

    results['inputs'] = inputs;

    const maxWeeklyAllowed = forecastSettings.maxWeeklyHours;


    let days = results['days'] ?? [];

    if (days?.find(d => d.state.includes('event_A'))) {
        const targetType = days?.find(d => d?.state?.includes('target_focus')) ? "goal" : "event";
        if (targetType == 'event') {
            let eventDay = days[days.length - 3];
            const constraint = days[days.length-1].constraint
            const restDays = Math.max(eventDay.after.t_rest_l, Math.max(eventDay.after.t_rest_h, eventDay.after.t_rest_p));
            const restDaysOver = Math.trunc((restDays - forecastSettings.eventRestDays)*10.0)/10.0 // Round to 10ths
            const maxWeekly = Math.round(Math.max.apply(null,days?.map(function (d) {return d.weekly_hours; })));
            forecastData = [...prependData, ...days];
            if (constraint > 0) {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 303,
                    status: "error",
                    results: results,
                }
                // forecastMessage = "A training plan that reaches your target could not be found.  Your target is not achievable.";
            } else if (restDaysOver > 0) {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 201,
                    status: "warning",
                    results: results
                }
                // forecastMessage = "An optimal training plan was found but there is very little flexibility.  Your target is barely achievable.";
            } else if ( maxWeekly > maxWeeklyAllowed) {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 202,
                    status: "warning",
                    weeklyHours: maxWeekly,
                    results: results
                }
                // forecastMessage = "An optimal training plan was found but will reach a high of "+maxWeekly+" hours of training per week during the plan.";
            } else {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 100,
                    status: "fine",
                    results: results
                }
                // forecastMessage = "An optimal training plan was found and your target is achievable.";
            }
        }
        else if (days.length > 1) { //targetType is goal
            const constraint = days[days.length-2].constraint
            const finalFp = Math.round(days?.find(d => d.state.includes('event_A'))?.fp);
            const targetFp = Math.round(inputs['days']?.find(d => d?.state?.includes('event_A'))?.focus_power);
            const ltlDiff = days?.find(d => d.state.includes('event_A'))?.tls.l - days?.find(d => d.state.includes('event_A'))?.ltl_target;
            const htlDiff = days?.find(d => d.state.includes('event_A'))?.tls.h - days?.find(d => d.state.includes('event_A'))?.htl_target;
            const ptlDiff = days?.find(d => d.state.includes('event_A'))?.tls.p - days?.find(d => d.state.includes('event_A'))?.ptl_target;
            const maxWeekly = Math.round(Math.max.apply(null,days?.map(function (d) {return d.weekly_hours; })));
            forecastData = [...prependData, ...days];
            if (constraint > 0) {
                // Constraints broken, not valid solution
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 303,
                    status: "error",
                    results: results,
                }
                // forecastMessage = "A training plan that reaches your target could not be found.  Your target is not achievable.";
            } else if (finalFp - targetFp < -1) {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 203,
                    status: "warning",
                    finalPower: finalFp,
                    results: results
                }
                // forecastMessage = "An optimal training plan was found but your target power goal was not reached. Final power reached was "+finalFp+"W.";
            } else if (ltlDiff < -2 || htlDiff < -0.5 || ptlDiff < -0.2) {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 204,
                    status: "warning",
                    results: results
                }
                // forecastMessage = "An optimal training plan was found but your target training loads required some adjustments.";
            } else {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 100,
                    status: "fine",
                    results: results
                }
                // forecastMessage = "An optimal training plan was found and your target is achievable.";

            }
            if (!(forecastInfo.status === "error") && maxWeekly > maxWeeklyAllowed) {
                forecastInfo = {
                    ...forecastInfo,
                    resultCode: 202,
                    status: "warning",
                    weeklyHours: maxWeekly,
                    results: results
                }
                // forecastMessage += " You will reach a high of "+maxWeekly+" hours of training per week during the plan.";
            }
        }
    }
    else {
        forecastInfo = {
            ...forecastInfo,
            resultCode: 302,
            status: "error",
            results: results
        }
        // forecastMessage = "Forecast unavailable. Use a target date further into the future.";          
    }

    if (adapt && (forecastInfo.status !== "error") && forecastInfo.results) {
        mergeAdaptedForecast($trainingPlan, inputs, forecastInfo.results)

        // Adapted set to false because this is effectively a new forecast,
        // with potentially a new event date, training load targets, etc.
        forecastInfo.results['adapted'] = false
        forecastInfo.results['keep_pinned'] = true
    }


    forecastData = [...prependData, ...days];

    forecastInfo.running = false;
    forecastInfo.done = true
    chartRange = null;
    
  }

  export function cancelForecast() {
    if (forecastInfo.running){
      forecastInfo.running = false;
      killWorker();
      // Re-assign previous value to reset chart
      forecastInfo.results = forecastInfo.results
    }
    forecastData = []
    forecastMessage = "";
    forecastProgress = null;
    chartRange = null;
    forecastInfo = {...defaultForecastInfo}
  }

</script>

{#if !hidden}
    <div out:slide={{duration:600, delay:600}}>
        <div class={cn("flex flex-col min-h-[450px]", className)} {id}>

            <!-- Progress Bar / Forecast Message -->
            {#if forecastInfo.running}
                <span out:slide={{delay:600, duration:600}}>
                    <ProgressBar progress={forecastProgress} message={forecastMessage} />
                </span>
            {/if}

            <!-- Chart -->
            {#if forecastData?.length || forecastInfo.running}
                <Am5Loader>
                    <ProgressionChart id='training-forecaster-chart' athletes={$userParams.athletes} data={forecastData} range={chartRange} />
                </Am5Loader>
            {:else} 
                <div class="grid grid-cols-[auto,75px] w-full gap-3">
                    <Skeleton class="h-[25px]"/>
                    <Skeleton class="h-[400px] row-start-2 "/>
                    <Skeleton class="h-[400px] row-start-2"/>
                    <Skeleton class="h-[20px] w-[85%] row-start-3"/>
                </div>
            {/if}

        </div>
    </div>
{/if}
